import React, { useState, useEffect } from 'react';
import { CHATBOT_RESTRICTED_GROUPS } from '../../config/featureAccess';

// ─── helpers ─────────────────────────────────────────────────────────────────

function transformAutoCrashData(apiResponse) {
  return {
    filesUploaded:  apiResponse.stats.filesUploaded,
    filesProcessed: apiResponse.stats.filesProcessed,
    files: apiResponse.processedFiles.map(file => ({
      name:   file.filename,
      status: 'Completed',
      timestamp: new Date().toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
      }),
      problem: {
        crashArea:       file.problem.crashArea,
        errorSignature:  file.problem.errorSignature,
        softwarePl:      file.problem.softwarePl,
        error:           file.problem.crashError,
      },
      fixes: file.cr_matched_data.map(cr => ({
        cr:             cr.changeRequestId,
        crTitle:        cr.changeRequestTitle,
        reportedBuild:  cr.reportedBuild,
        dateFound:      cr.dateIssueFound,
        buildFixed:     cr.fixedBuild,
        foundOnImage:   cr.matchedImage,
        foundOnProduct: cr.matchedProduct,
      })),
    })),
  };
}

function extractUniqueCRs(files) {
  const crMap = new Map();
  files.forEach(file => {
    const seen = new Set();
    file.fixes.forEach(fix => {
      seen.add(fix.cr);
      if (crMap.has(fix.cr)) {
        crMap.get(fix.cr).totalRecommendations++;
      } else {
        crMap.set(fix.cr, { id: fix.cr, title: fix.crTitle, fileCount: 0, totalRecommendations: 1 });
      }
    });
    seen.forEach(id => crMap.get(id).fileCount++);
  });
  return Array.from(crMap.values()).sort((a, b) =>
    b.fileCount !== a.fileCount ? b.fileCount - a.fileCount : b.totalRecommendations - a.totalRecommendations
  );
}

// ─── RdAutoCrashPanel ─────────────────────────────────────────────────────────
// Props:
//   sessionId   – the RD session ID to stream
//   userGroups  – array of user group strings (access gate)
//   onClose     – called when the user dismisses the panel

function RdAutoCrashPanel({ sessionId, userGroups = [], onClose }) {
  const isRestricted = userGroups.some(g => CHATBOT_RESTRICTED_GROUPS.includes(g));

  const [data, setData]               = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [fileFilter, setFileFilter]   = useState('');

  useEffect(() => {
    if (isRestricted || !sessionId) return;

    const sseUrl = `${process.env.REACT_APP_WBC_API_BASE_URL}/api/auto-crash/session-stream?sessionId=${sessionId}`;
    const es = new EventSource(sseUrl);

    es.onopen = () => setIsConnected(true);
    es.onerror = () => setIsConnected(false);

    const handleData = (event) => {
      try { setData(transformAutoCrashData(JSON.parse(event.data))); } catch (_) {}
    };
    es.addEventListener('auto-crash-data',   handleData);
    es.addEventListener('auto-crash-update', handleData);

    return () => { es.close(); };
  }, [sessionId, isRestricted]);

  if (isRestricted) return null;

  const files     = data?.files || [];
  const uniqueCRs = extractUniqueCRs(files);
  const shownFiles = fileFilter && fileFilter !== 'All Files'
    ? files.filter(f => f.name === fileFilter)
    : fileFilter === 'All Files' ? files : [];

  return (
    <div className="auto-crash-container">

      {/* Banner */}
      <div className="transaction-banner">
        <div className="transaction-id">
          <i className="material-icons">fingerprint</i>
          Session ID: {sessionId}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isConnected && (
            <div className="live-indicator">
              <div className="pulse"></div>
              Live Updates
            </div>
          )}
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#64748b' }}
            title="Close"
          >✕</button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-bar">
        <div className="stat-block files-uploaded">
          <i className="material-icons stat-icon">file_upload</i>
          <div className="stat-number">{data?.filesUploaded ?? 0}</div>
          <div className="stat-label">Files Uploaded</div>
        </div>
        <div className="stat-block files-processed">
          <i className="material-icons stat-icon">check_circle</i>
          <div className="stat-number">{data?.filesProcessed ?? 0}</div>
          <div className="stat-label">Files Processed</div>
        </div>
      </div>

      {/* Recommended CR Fixes */}
      <div className="recommended-cr-section">
        <div className="cr-section-header">
          <i className="material-icons">build</i>
          <span>Recommended CR Fixes</span>
          <span className="cr-count">({uniqueCRs.length} unique CR{uniqueCRs.length !== 1 ? 's' : ''})</span>
        </div>
        <div className="cr-list">
          {uniqueCRs.length > 0 ? uniqueCRs.map((cr, idx) => (
            <div key={idx} className="cr-item">
              <span className="cr-id">{cr.id}</span>
              <span className="cr-separator">-</span>
              <span className="cr-title">{cr.title}</span>
              {(cr.fileCount > 1 || cr.totalRecommendations > cr.fileCount) && (
                <span className="cr-frequency">
                  ({cr.fileCount} {cr.fileCount === 1 ? 'file' : 'files'},{' '}
                  {cr.totalRecommendations} {cr.totalRecommendations === 1 ? 'recommendation' : 'recommendations'})
                </span>
              )}
            </div>
          )) : (
            <div className="cr-empty-state">No CR recommendations available yet</div>
          )}
        </div>
      </div>

      {/* File Results */}
      <div className="file-results">
        <div className="file-controls">
          <div className="filter-controls">
            <select
              className="filter-select"
              value={fileFilter}
              onChange={e => setFileFilter(e.target.value)}
            >
              <option value="">No File</option>
              <option value="All Files">All Files</option>
              {files.map((f, i) => <option key={i} value={f.name}>{f.name}</option>)}
            </select>
          </div>
        </div>

        {files.length === 0 ? (
          data?.filesUploaded > 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#757575' }}>
              <i className="material-icons" style={{ fontSize: 48, marginBottom: 10 }}>hourglass_empty</i>
              <p>Waiting for files to be processed…</p>
            </div>
          ) : (
            <div style={{ padding: '20px', textAlign: 'center', color: '#757575' }}>
              <i className="material-icons" style={{ fontSize: 48, marginBottom: 10 }}>cloud_off</i>
              <p>No files uploaded</p>
            </div>
          )
        ) : !fileFilter ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#757575' }}>
            <i className="material-icons" style={{ fontSize: 48, marginBottom: 10 }}>folder_open</i>
            <p>Please select a file from the dropdown to view details</p>
          </div>
        ) : (
          shownFiles.map((file, fileIdx) => (
            <div key={fileIdx} className="file-card">
              <div className="file-header">
                <div className="file-name">
                  <i className="material-icons">description</i>
                  {file.name}
                </div>
                <div className="file-status">
                  <span className={`status-badge ${file.status === 'Completed' ? 'status-completed' : 'status-processing'}`}>
                    {file.status}
                  </span>
                </div>
              </div>
              <div className="file-content">
                <div className="crash-section problem">
                  <div className="crash-section-header problem">
                    <i className="material-icons">report_problem</i>
                    Problem
                  </div>
                  <div className="crash-section-content">
                    <div className="crash-details">
                      <div className="crash-item"><span className="crash-label">Crash Area:</span><span className="crash-value">{file.problem.crashArea}</span></div>
                      <div className="crash-item"><span className="crash-label">Error Signature:</span><span className="crash-value">{file.problem.errorSignature}</span></div>
                      <div className="crash-item"><span className="crash-label">Software PL:</span><span className="crash-value">{file.problem.softwarePl}</span></div>
                      <div className="crash-item"><span className="crash-label">Error:</span><span className="crash-value">{file.problem.error}</span></div>
                    </div>
                  </div>
                </div>
                <div className="crash-section fix">
                  <div className="crash-section-header fix">
                    <i className="material-icons">check_circle</i>
                    Recommended Fix
                  </div>
                  <div className="crash-section-content">
                    {file.fixes.map((fix, fixIdx) => (
                      <div key={fixIdx} className="cr-match">
                        <div className="crash-item"><span className="crash-label">CR:</span><span className="crash-value">{fix.cr}</span></div>
                        <div className="crash-item"><span className="crash-label">CR Title:</span><span className="crash-value">{fix.crTitle}</span></div>
                        <div className="crash-item"><span className="crash-label">Reported Build:</span><span className="crash-value">{fix.reportedBuild}</span></div>
                        <div className="crash-item"><span className="crash-label">Date Found:</span><span className="crash-value">{fix.dateFound}</span></div>
                        <div className="crash-item"><span className="crash-label">Build Fixed:</span><span className="crash-value">{fix.buildFixed}</span></div>
                        <div className="crash-item"><span className="crash-label">Found On Image:</span><span className="crash-value">{fix.foundOnImage}</span></div>
                        <div className="crash-item"><span className="crash-label">Found On Product:</span><span className="crash-value">{fix.foundOnProduct}</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default RdAutoCrashPanel;
